import classNames from 'classnames';
import TooltipForText from 'components/Tooltips/TooltipForText';
import { TooltipForTextNew } from 'components/Tooltips/TooltipForTextNew';
import { TOptions } from 'i18next';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import style from './Text.module.scss';
import { TTextTranslationNamespaces } from 'types';

type TTextThemes =
	| 'neutralDark'
	| 'neutralMedium'
	| 'neutralLight'
	| 'secondary'
	| 'secondaryLight'
	| 'secondaryFaded'
	| 'contrast'
	| 'accent'
	| 'error'
	| 'errorDark';
type TTextWeight = 'thin' | 'medium' | 'bold';
type TTextAlign = 'left' | 'center' | 'right';
type TTextSize = 'xs' | 's' | 'm' | 'ml' | 'l' | 'xl' | 'xxl';
type TTextDisplay = 'block' | 'inline';

interface IProps {
	translationKey: string | null | undefined;
	translationNameSpace?: TTextTranslationNamespaces;
	translationOptions?: TOptions | string;
	className?: string;
	cssStyle?: React.CSSProperties;
	align?: TTextAlign;
	theme?: TTextThemes;
	size?: TTextSize;
	weight?: TTextWeight;
	display?: TTextDisplay;
	asFragment?: boolean;
	components?: React.ReactNode[] | { [tagName: string]: React.ReactNode };
	values?: {};
	withTooltip?: boolean;
	isNewTooltip?: boolean;
}

const Text: React.FC<IProps> = ({
	translationKey,
	translationNameSpace,
	translationOptions,
	className,
	cssStyle,
	align = 'left',
	theme = 'neutralDark',
	size = 'm',
	weight = 'medium',
	display = 'block',
	asFragment = false,
	components,
	values,
	withTooltip = false,
	isNewTooltip = false,
	...restProps
}) => {
	const { t } = useTranslation(translationNameSpace);

	const textClasses = classNames(
		style.text,
		{ [style.tooltipOverflow]: withTooltip },
		{ [style.inline]: display === 'inline' },
		style[align],
		style[theme],
		style[size],
		style[weight],
		className,
	);

	const text = translationNameSpace ? t(translationKey || '-', translationOptions) : translationKey || '-';

	const renderWithTooltip = (content: React.ReactNode) =>
		isNewTooltip ? <TooltipForTextNew>{content}</TooltipForTextNew> : <TooltipForText>{content}</TooltipForText>;

	if (asFragment) {
		return <>{text}</>;
	}

	if (components?.length) {
		const withComponentTemplate = () => (
			<Trans t={t} i18nKey={translationKey || '-'} components={components} values={values} />
		);

		const withTooltipWrapper = () => renderWithTooltip(withComponentTemplate());

		return (
			<div style={cssStyle} className={textClasses} {...restProps}>
				{withTooltip ? withTooltipWrapper() : withComponentTemplate()}
			</div>
		);
	}

	if (withTooltip) {
		return (
			<div style={cssStyle} className={textClasses} {...restProps}>
				{renderWithTooltip(text)}
			</div>
		);
	}

	return (
		<div style={cssStyle} className={textClasses} {...restProps}>
			{text}
		</div>
	);
};

export default React.memo(Text);
